import {defineStore} from 'pinia';
import {authService, setAuthHeader} from '../services/api';
import {useDataStore} from '@/stores/dataStore.js'

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null,
        token: null,
        isLoading: false,
        error: null,
        currentUser: null // Will be determined based on login state
    }),

    getters: {
        isLoggedIn: (state) => !!state.token,
        userWallet: (state) => {
            const dataStore = useDataStore();
            return dataStore.userWallet;
        },
        formattedWallet: (state) => {
            if (!state.user) return '0';

            const walletType = state.user.is_da;
            let balance, symbol;

            switch (walletType.toString()) {
                case "2": // DZD
                    balance = state.user.wallet_da;
                    symbol = 'Dzd';
                    break;
                case "1": // Euro
                    balance = state.user.wallet_euro;
                    symbol = '€';
                    break;
                case "3": // EGP
                    balance = state.user.wallet_egp;
                    symbol = 'EGP';
                    break;
                case "0": // USD
                    balance = state.user.wallet;
                    symbol = '$';
                    break;
                default: // USD
                    balance = state.user.wallet;
                    symbol = 'NA';
            }
            return `${parseFloat(balance).toFixed(2)} ${symbol}`;
        },
        userName: (state) => {
            // If user is logged in, return their actual name
            if (state.user?.name) return state.user.name;
            // If currentUser is explicitly set, use that
            if (state.currentUser) return state.currentUser;
            // Default fallback to 'guest'
            return 'guest';
        },

        // Get user currency preference
        userCurrencyType: (state) => state.user?.is_da || 0,

        // Get price visibility preference
        shouldHidePrices: (state) => state.user?.hide_price === 1,

        shouldUseDAPricing:() => useDataStore().shouldUseDAPricing,

        shouldShowFlexy: (state) => state.user?.is_flexy,


    },

    actions: {
        async login(email, password, recaptchaToken ) {
            this.isLoading = true;
            this.error = null;

            try {
                const response = await authService.login(email, password, recaptchaToken );
                const dataStore = useDataStore();
                console.log('Login response:', response);

                // Set user and token in state
                this.user = response.user;
                this.token = response.token;

                // Set currentUser to the logged in user's actual name
                this.currentUser = response.user.name;

                // Also store in localStorage for persistence
                localStorage.setItem('token', response.token);
                localStorage.setItem('user', JSON.stringify(response.user));

                // Set authorization header for future requests
                setAuthHeader(response.token);

                // Update the DataStore with user information for currency display
                dataStore.setUserData(response.user);
                dataStore.setUserDisplayDa(!response.user.is_active);

                return response;
            } catch (error) {
                console.log('Login error:', error.message);
                this.error = error.message;
                throw error;
            } finally {
                this.isLoading = false;
            }
        },

        async logout() {
            try {
                await authService.logout();
                const dataStore = useDataStore();

                // Clear state
                this.user = null;
                this.token = null;

                // Reset auth header
                setAuthHeader(null);

                // Reset currentUser to 'guest' when logged out
                this.currentUser = 'guest';

                // Reset DataStore user data
                dataStore.setUserData(null);

                // Clear localStorage
                localStorage.removeItem('token');
                localStorage.removeItem('user');

            } catch (error) {
                console.error('Logout error:', error);
            }
        },


        async register({ fullName, email, phone, password, recaptchaToken }) {
            this.isLoading = true;
            this.error = null;

            try {
                // Call the backend register API (assuming authService.register exists)
                return await authService.register({
                    fullName,
                    email,
                    phone,
                    password,
                    recaptchaToken
                });
            } catch (error) {
                console.log('Registration error:', error.message);
                this.error = error.message;
                throw error;
            } finally {
                this.isLoading = false;
            }
        },
        updateWalletBalance(newBalance) {
            if (!this.user) return;

            // Update the appropriate wallet based on currency type
            const walletType = this.user.is_da;

            switch (walletType.toString()) {
                case "2": // DZD
                    this.user.wallet_da = parseFloat(newBalance);
                    break;
                case "1": // Euro
                    this.user.wallet_euro = parseFloat(newBalance);
                    break;
                case "3": // EGP
                    this.user.wallet_egp = parseFloat(newBalance);
                    break;
                default: // USD
                    this.user.wallet = parseFloat(newBalance);
            }

            // Update localStorage to persist changes
            localStorage.setItem('user', JSON.stringify(this.user));
        },

        initAuth() {
            // Initialize auth state from localStorage
            const token = localStorage.getItem('token');
            const user = localStorage.getItem('user');

            if (token && user) {
                try {
                    const userData = JSON.parse(user);
                    this.token = token;
                    this.user = userData;

                    // Set currentUser to the user's actual name
                    this.currentUser = userData.name;

                    // Set user data in the DataStore
                    const dataStore = useDataStore();
                    dataStore.setUserData(userData);

                    // Set authorization header
                    setAuthHeader(token);
                } catch (e) {
                    console.error('Failed to parse user data from localStorage');
                    // Clear invalid data
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    // Set to guest since auth failed
                    this.currentUser = 'guest';
                }
            } else {
                // No token/user found, set to guest
                this.currentUser = 'guest';
            }

        },

        updateUserWallet(userNew){

            if (!this.user) {
                console.error('Cannot update wallet: No user logged in');
                return;
            }

            // Safely update each wallet with nullish coalescing
            // Ensure we're dealing with numbers
            this.user.wallet = parseFloat(userNew?.wallet ?? this.user.wallet) || 0;
            this.user.wallet_da = parseFloat(userNew?.wallet_da ?? this.user.wallet_da) || 0;
            this.user.wallet_euro = parseFloat(userNew?.wallet_euro ?? this.user.wallet_euro) || 0;
            this.user.wallet_egp = parseFloat(userNew?.wallet_egp ?? this.user.wallet_egp) || 0;

            this.user.is_da = parseFloat(userNew?.is_da ?? this.user.is_da) || 0;


            // Update localStorage to persist changes
            localStorage.setItem('user', JSON.stringify(this.user));

            return 'Updated user wallets';
        }
    },

    persist: true
});